import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { WidgetCustomizationModal } from '@/components/dashboard/widget-customization-modal';
import type { DashboardType, UserWidgetPreference } from '@/types/dashboard';
import { useDashboard } from '@/hooks/use-dashboard';
import { useFinancialWidgets } from '@/hooks/use-financial-widgets';
import { dataService } from '@/services/data-service';
import type { Transaction } from '@/types/asset';

// Import existing widget components
import { AssetsByCategoryCard } from '@/components/dashboard/assets-by-category-card';
import { AssetsByLocationCard } from '@/components/dashboard/assets-by-location-card';
import { AssetsByStatusCard } from '@/components/dashboard/assets-by-status-card';
import { DepreciationYTDCard } from '@/components/dashboard/depreciation-ytd-card';
import { NetBookValueCard } from '@/components/dashboard/net-book-value-card';
import { UpcomingInsuranceRenewalsCard } from '@/components/dashboard/upcoming-insurance-renewals-card';

// Import financial widget components (Phase 3)
import {
  TotalAssetValueCard,
  AssetValueTrendCard,
  DepreciationByCategoryCard,
  TopAssetsByValueCard,
  WarrantyStatusCard,
  QuickActionsFinancial,
  QuickActionsAssetManagement,
  KpiTotalAssetValue,
  KpiAcquisitionCost,
  KpiTotalDepreciation,
  KpiAverageAssetValue,
  KpiCurrentPeriod,
  KpiTotalAssets,
  KpiActiveAssets,
  KpiMaintenanceDue,
  KpiAuditsPending,
  KpiAssetHealth,
} from '@/components/dashboard/widgets';

// Import Asset Management widget components (Phase 4)
import {
  AssetHealthWidget,
  AssetHealthOverviewWidget,
  MaintenanceInsuranceDueCalendarWidget,
  AuditStatusWidget,
  ExceptionAlertsWidget,
  LifecycleStageWidget,
  AssetAgeAnalysisWidget,
} from '@/components/dashboard/widgets/asset-management';

// Import Compliance widget components (Phase 5)
import { ComplianceSummaryWidget } from '@/components/dashboard/widgets/compliance-summary-widget';
import { UpcomingInspectionsWidget } from '@/components/dashboard/widgets/upcoming-inspections-widget';
import { OverdueInspectionsWidget } from '@/components/dashboard/widgets/overdue-inspections-widget';

// Import placeholder components for widgets that don't exist yet
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package } from 'lucide-react';
import { WidgetWrapper } from '@/components/dashboard/widget-wrapper';

// Temporary placeholder component for widgets that don't exist yet
function PlaceholderWidget({ widgetId, icon: Icon }: { readonly widgetId: string; readonly icon: React.ComponentType<{ className?: string }> }) {
  const widgetNames: Record<string, string> = {
    'total-asset-value': 'Total Asset Value',
    'asset-value-trend': 'Asset Value Trend',

    'depreciation-by-category': 'Depreciation by Category',
    'top-assets-by-value': 'Top Assets by Value',
    'warranty-status': 'Warranty Status',
    'financial-summary': 'Financial Summary',
    'kpi-total-assets': 'Total Assets',
    'kpi-active-assets': 'Active Assets',
    'kpi-maintenance-due': 'Maintenance Due',
    'kpi-audits-pending': 'Audits Pending',
    'kpi-exceptions': 'Exceptions',
    'kpi-asset-health': 'Asset Health',
    'asset-health-score': 'Asset Health Score',
    'maintenance-overview': 'Maintenance Overview',
    'asset-lifecycle': 'Asset Lifecycle',
    'maintenance-schedule': 'Maintenance Schedule',
    'asset-utilization': 'Asset Utilisation',
    'asset-alerts': 'Asset Alerts',
    'maintenance-costs': 'Maintenance Costs',
    'asset-performance': 'Asset Performance',
    'location-overview': 'Location Overview',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          {widgetNames[widgetId] || widgetId}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">Coming Soon</div>
        <p className="text-xs text-muted-foreground mt-2">
          This widget will be implemented in Phase 3/4
        </p>
      </CardContent>
    </Card>
  );
}

export function DashboardNew() {
  // Custom breakpoint hooks for progressive KPI hiding
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  const [currentDashboard, setCurrentDashboard] = useState<DashboardType>(() => {
    // Load from localStorage on initial render
    try {
      const saved = localStorage.getItem('selectedDashboard');
      return (saved as DashboardType) ?? 'asset-management';
    } catch {
      return 'asset-management';
    }
  });
  const [dashboardInitialized, setDashboardInitialized] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [insuranceRenewals, setInsuranceRenewals] = useState<any[]>([]);
  const [netBookValueTrend, setNetBookValueTrend] = useState<any>({ currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] });
  const [depreciationYTDTrend, setDepreciationYTDTrend] = useState<any>({ currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] });
  const [loading, setLoading] = useState(true);

  const { useUserPreferences } = useDashboard();
  const { data: userPrefs } = useUserPreferences();

  // Update current dashboard when user preferences load from backend (only once on initial load)
  useEffect(() => {
    if (userPrefs?.selectedDashboardType && !dashboardInitialized) {
      setCurrentDashboard(userPrefs.selectedDashboardType);
      // Also update localStorage to sync with backend
      try {
        localStorage.setItem('selectedDashboard', userPrefs.selectedDashboardType);
      } catch (error) {
        console.error('Failed to sync dashboard preference to localStorage:', error);
      }
      setDashboardInitialized(true);
    }
  }, [userPrefs, dashboardInitialized]);

  const { useChartSummary } = useFinancialWidgets();
  const { data: chartSummary, isLoading: chartLoading } = useChartSummary();

  // Load dashboard data for old widgets
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [transactionsData, insuranceRenewalsData, nbvTrendData, depreciationTrendData] = await Promise.all([
        dataService.getTransactions({ pageSize: 10000 }).catch(() => ({ data: [], total: 0, page: 1, pageSize: 10000, totalPages: 0 })),
        dataService.getUpcomingInsuranceRenewals(90).catch(() => []),
        dataService.getNetBookValueTrend().catch(() => ({ currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] })),
        dataService.getDepreciationYTDTrend().catch(() => ({ currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] })),
      ]);

      const transactionsArray = Array.isArray(transactionsData) ? transactionsData : transactionsData.data ?? [];

      setTransactions(transactionsArray);
      setInsuranceRenewals(insuranceRenewalsData);
      setNetBookValueTrend(nbvTrendData);
      setDepreciationYTDTrend(depreciationTrendData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const dynamicChartData = {
    assetsByCategory: (chartSummary?.byCategory ?? []).map(c => ({
      category: c.category,
      value: c.netBookValue,
      count: c.count,
      color: '#6b7280',
    })),
    assetsByLocation: (chartSummary?.byLocation ?? []).map(l => ({
      location: l.location,
      value: l.netBookValue,
      count: l.count,
      color: '#6b7280',
    })),
    assetsByStatus: (chartSummary?.byStatus ?? []).map(s => ({
      name: s.displayName,
      value: s.netBookValue,
      count: s.count,
      fill: '#6b7280',
      status: s.status,
    })),
  };

  const renderWidget = (widget: UserWidgetPreference) => {
    const widgetId = widget.widgetId;

    // Show loading state for old widgets
    if ((loading || chartLoading) && ['assets-by-category', 'assets-by-location', 'assets-by-status', 'depreciation-overview', 'net-book-value', 'insurance-compliance'].includes(widgetId)) {
      return (
        <WidgetWrapper key={widgetId} colSpan={2}>
          <Card>
            <CardHeader>
              <CardTitle>Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse h-48 bg-muted rounded" />
            </CardContent>
          </Card>
        </WidgetWrapper>
      );
    }

    // Render existing components with actual data
    switch (widgetId) {
      // Quick Actions - Full width, no wrapper (rendered outside grid)
      case 'quick-actions-financial':
        return <QuickActionsFinancial key={widgetId} />;

      case 'quick-actions-asset-management':
        return <QuickActionsAssetManagement key={widgetId} />;

      // KPI Widgets - rendered in special 5-column grid (no wrapper needed)
      case 'kpi-total-asset-value':
        return <KpiTotalAssetValue key={widgetId} />;

      case 'kpi-acquisition-cost':
        return <KpiAcquisitionCost key={widgetId} />;

      case 'kpi-total-depreciation':
        return <KpiTotalDepreciation key={widgetId} />;

      case 'kpi-average-asset-value':
        return <KpiAverageAssetValue key={widgetId} />;

      case 'kpi-current-period':
        return <KpiCurrentPeriod key={widgetId} />;

      // Asset Management KPIs - rendered in special 5-column grid (no wrapper needed)
      case 'kpi-total-assets':
        return <KpiTotalAssets key={widgetId} />;

      case 'kpi-active-assets':
        return <KpiActiveAssets key={widgetId} />;

      case 'kpi-maintenance-due':
        return <KpiMaintenanceDue key={widgetId} />;

      case 'kpi-audits-pending':
        return <KpiAuditsPending key={widgetId} />;

      case 'kpi-asset-health':
        return <KpiAssetHealth key={widgetId} />;

      // Chart Widgets - 2 columns each
      case 'assets-by-category':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <AssetsByCategoryCard data={dynamicChartData.assetsByCategory} />
          </WidgetWrapper>
        );

      case 'assets-by-location':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <AssetsByLocationCard data={dynamicChartData.assetsByLocation} />
          </WidgetWrapper>
        );

      case 'assets-by-status':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <AssetsByStatusCard data={dynamicChartData.assetsByStatus} />
          </WidgetWrapper>
        );

      case 'depreciation-overview':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <DepreciationYTDCard
              currentValue={depreciationYTDTrend.currentValue}
              monthlyData={depreciationYTDTrend.monthlyData}
              assets={[]}
              transactions={transactions}
            />
          </WidgetWrapper>
        );

      case 'net-book-value':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <NetBookValueCard
              currentValue={netBookValueTrend.currentValue}
              monthlyData={netBookValueTrend.monthlyData}
              assets={[]}
            />
          </WidgetWrapper>
        );

      case 'insurance-compliance':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <UpcomingInsuranceRenewalsCard renewals={insuranceRenewals} />
          </WidgetWrapper>
        );

      // Financial Dashboard Widgets
      case 'total-asset-value':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <TotalAssetValueCard />
          </WidgetWrapper>
        );

      case 'asset-value-trend':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <AssetValueTrendCard />
          </WidgetWrapper>
        );

      case 'depreciation-by-category':
        return (
          <WidgetWrapper key={widgetId} colSpan={4}>
            <DepreciationByCategoryCard />
          </WidgetWrapper>
        );


      case 'top-assets-by-value':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <TopAssetsByValueCard />
          </WidgetWrapper>
        );

      case 'warranty-status':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <WarrantyStatusCard />
          </WidgetWrapper>
        );

      // Asset Management Dashboard Widgets
      case 'asset-mgmt-health-overview':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <AssetHealthWidget />
          </WidgetWrapper>
        );

      case 'asset-mgmt-maintenance-insurance-due':
        return (
          <WidgetWrapper key={widgetId} colSpan={4}>
            <MaintenanceInsuranceDueCalendarWidget />
          </WidgetWrapper>
        );

      case 'asset-mgmt-audit-status':
        return (
          <WidgetWrapper key={widgetId} colSpan={4}>
            <AuditStatusWidget />
          </WidgetWrapper>
        );

      case 'asset-mgmt-exception-alerts':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <ExceptionAlertsWidget />
          </WidgetWrapper>
        );

      case 'asset-health-score':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <AssetHealthOverviewWidget />
          </WidgetWrapper>
        );

      case 'asset-mgmt-lifecycle-stage':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <LifecycleStageWidget />
          </WidgetWrapper>
        );

      case 'asset-mgmt-age-analysis':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <AssetAgeAnalysisWidget />
          </WidgetWrapper>
        );

      case 'asset-mgmt-recent-activity':
        // Remove this widget from the dashboard
        return null;

      // Compliance Dashboard Widgets (Phase 5)
      case 'compliance-summary':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <ComplianceSummaryWidget />
          </WidgetWrapper>
        );

      case 'upcoming-inspections':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <UpcomingInspectionsWidget />
          </WidgetWrapper>
        );

      case 'overdue-inspections':
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <OverdueInspectionsWidget />
          </WidgetWrapper>
        );

      // Deprecated widgets - return null to prevent errors
      case 'cost-breakdown':
      case 'financial-summary':
        return null;

      default:
        return (
          <WidgetWrapper key={widgetId} colSpan={2}>
            <PlaceholderWidget widgetId={widgetId} icon={Package} />
          </WidgetWrapper>
        );
    }
  };

  return (
    <>
      <DashboardLayout
        onCustomizeClick={() => setCustomizeModalOpen(true)}
        selectedDashboard={currentDashboard}
        onDashboardChange={setCurrentDashboard}
      >
        {(widgets) => {
          // Group widgets into KPIs and non-KPIs
          const allKpiWidgets = widgets.filter(w => w.widgetId.startsWith('kpi-'));
          const nonKpiWidgets = widgets.filter(w => !w.widgetId.startsWith('kpi-'));

          // Progressive KPI filtering based on screen width and dashboard type
          let kpiWidgets = allKpiWidgets;
          
          if (currentDashboard === 'financial') {
            // Financial Dashboard progressive hiding
            // >= 1500px: Show all 5 KPIs
            // 1300-1499px: Show 4 KPIs (hide Current Period)
            // < 1300px: Show 3 KPIs (Total Asset Value, Average Asset Value, Acquisition Cost)
            if (screenWidth >= 1500) {
              // Show all KPIs
              kpiWidgets = allKpiWidgets;
            } else if (screenWidth >= 1300) {
              // Hide Current Period
              kpiWidgets = allKpiWidgets.filter(w => w.widgetId !== 'kpi-current-period');
            } else {
              // Show only 3 most important
              const financialKpis = ['kpi-total-asset-value', 'kpi-average-asset-value', 'kpi-acquisition-cost'];
              kpiWidgets = allKpiWidgets.filter(w => financialKpis.includes(w.widgetId));
            }
          } else if (currentDashboard === 'asset-management') {
            // Asset Management Dashboard progressive hiding
            // >= 1500px: Show all 5 KPIs
            // 1300-1499px: Show 4 KPIs (hide Audits Pending)
            // < 1300px: Show 3 KPIs (Total Assets, Active Assets, Maintenance Due)
            if (screenWidth >= 1500) {
              // Show all KPIs
              kpiWidgets = allKpiWidgets;
            } else if (screenWidth >= 1300) {
              // Hide Audits Pending
              kpiWidgets = allKpiWidgets.filter(w => w.widgetId !== 'kpi-audits-pending');
            } else {
              // Show only 3 most important
              const maintenanceKpis = ['kpi-total-assets', 'kpi-active-assets', 'kpi-maintenance-due'];
              kpiWidgets = allKpiWidgets.filter(w => maintenanceKpis.includes(w.widgetId));
            }
          }

          return (
            <>
              {/* Render KPIs in a progressive grid based on screen width */}
              {kpiWidgets.length > 0 && (
                <div className="col-span-2 md:col-span-4 xl:col-span-6">
                  <div className="grid gap-4" style={{ 
                    gridTemplateColumns: screenWidth >= 1300 
                      ? `repeat(${screenWidth >= 1500 ? 5 : 4}, minmax(0, 1fr))` 
                      : 'repeat(3, minmax(0, 1fr))'
                  }}>
                    {kpiWidgets.map((widget) => (
                      <div key={widget.widgetId}>
                        {renderWidget(widget)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Render other widgets normally */}
              <div className="contents">
                {nonKpiWidgets.map((widget) => renderWidget(widget))}
              </div>
            </>
          );
        }}
      </DashboardLayout>

      <WidgetCustomizationModal
        open={customizeModalOpen}
        onOpenChange={setCustomizeModalOpen}
        dashboardType={currentDashboard}
      />
    </>
  );
}

export default DashboardNew;
